import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { Play, Square, FileText, Clock, ChevronRight, Sparkles } from 'lucide-react'
import { Project } from '../../types'
import ProjectSelector from './ProjectSelector'

// 近期任務項目介面
export interface RecentTask {
  description: string
  projectId: string
  projectName: string
}

interface TimerOperationCardProps {
  // 計時器狀態
  isRunning: boolean
  isLoading: boolean
  
  // 工作描述
  description: string
  onDescriptionChange: (value: string) => void
  
  // 專案相關
  projects: Project[]
  selectedProject: Project | null
  recentFavoriteProjects: Project[]
  onSelectProject: (project: Project | null) => void
  onReloadProjects?: () => Promise<void>
  
  // 控制按鈕
  onStart: () => void
  onStop: () => void
  
  // 近期任務相關
  recentTasks?: RecentTask[]
  onQuickStart?: (task: RecentTask) => void
  
  // 狀態
  disabled?: boolean
  className?: string
}

const TimerOperationCard: React.FC<TimerOperationCardProps> = ({
  isRunning,
  isLoading,
  description,
  onDescriptionChange,
  projects,
  selectedProject,
  recentFavoriteProjects,
  onSelectProject,
  onReloadProjects,
  onStart,
  onStop,
  recentTasks = [],
  onQuickStart,
  disabled = false,
  className = ''
}) => {
  const [isInputFocused, setIsInputFocused] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // 自動依內容調整高度，最多 3 行；超過則啟用內部捲動
  useLayoutEffect(() => {
    const ta = inputRef.current
    if (!ta) return
    // 先重置以正確量測 scrollHeight
    ta.style.height = 'auto'
    const styles = window.getComputedStyle(ta)
    const lineHeight = parseFloat(styles.lineHeight) || 24
    const paddingY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom)
    const borderY = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth)
    const maxHeight = lineHeight * 3 + paddingY + borderY
    const newHeight = Math.min(ta.scrollHeight, maxHeight)
    ta.style.height = `${newHeight}px`
    ta.style.overflowY = ta.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }, [description])

  // 點擊外部時關閉建議列表
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsInputFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // 處理快速選擇任務
  const handleQuickSelectTask = (task: RecentTask) => {
    if (onQuickStart) {
      onQuickStart(task)
    }
    setIsInputFocused(false)
  }

  // 判斷是否顯示近期任務建議
  const shouldShowRecentTasks = isInputFocused && description.length === 0 && recentTasks.length > 0 && !isRunning

  return (
    <div className={`glass-card rounded-2xl md:rounded-3xl p-5 md:p-8 ${className}`}>
      {/* 標題區 */}
      <div className="section-header mb-6">
        <div className="section-header-icon">
          <Sparkles className="w-5 h-5 text-primary-600" />
        </div>
        <h3 className="section-header-title">開始計時</h3>
      </div>
      
      {/* 任務名稱輸入 */}
      <div className="space-y-2 mb-6" ref={containerRef}>
        <label className="block text-sm font-medium text-secondary-600 mb-2">
          任務描述
        </label>
        
        <div className="relative">
          <div className="absolute top-3.5 left-4 pointer-events-none z-10">
            <FileText className={`
              w-5 h-5 transition-colors duration-300
              ${isInputFocused || description ? 'text-primary-500' : 'text-secondary-400'}
            `} />
          </div>
          
          <textarea
            ref={inputRef}
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            onFocus={() => setIsInputFocused(true)}
            disabled={disabled}
            placeholder="現在要做什麼？（請完整描述）"
            rows={1}
            className="input-modern pl-12 pr-4 resize-none leading-6 block"
          />
          
        </div>

        {/* 近期工作項目建議 */}
        {shouldShowRecentTasks && (
          <div className="mt-3 glass-card rounded-xl overflow-hidden border border-secondary-200/50 animate-fade-in">
            <div className="px-4 py-2.5 bg-gradient-to-r from-secondary-50 to-secondary-100/50 border-b border-secondary-200/50">
              <div className="flex items-center gap-2 text-xs font-medium text-secondary-600">
                <Clock className="w-3.5 h-3.5" />
                <span>近期工作項目</span>
              </div>
            </div>
            <div className="divide-y divide-secondary-100/50">
              {recentTasks.map((task, index) => (
                <button
                  key={`${task.description}-${task.projectId}-${index}`}
                  onClick={() => handleQuickSelectTask(task)}
                  disabled={disabled}
                  className="w-full px-4 py-3 text-left hover:bg-primary-50/50 
                    transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                    group/task"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-secondary-800 truncate group-hover/task:text-primary-700">
                        {task.description}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span 
                          className="w-2 h-2 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
                          style={{ backgroundColor: projects.find(p => p.id === task.projectId)?.color || '#6B7280' }}
                        />
                        <span className="text-xs text-secondary-500 truncate">{task.projectName}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-secondary-300 group-hover/task:text-primary-500 
                      group-hover/task:translate-x-1 transition-all flex-shrink-0 ml-3" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        
      </div>
      
      {/* 專案選擇 */}
      <div className="mb-8">
        <ProjectSelector
          projects={projects}
          selectedProject={selectedProject}
          recentFavoriteProjects={recentFavoriteProjects}
          onSelectProject={onSelectProject}
          onReloadProjects={onReloadProjects}
          disabled={disabled}
        />
      </div>
      
      {/* 控制按鈕區 */}
      <div className="space-y-4">
        {!isRunning ? (
          // 開始按鈕
          <button
            onClick={onStart}
            disabled={disabled || isLoading || !selectedProject || !description.trim()}
            className="btn-modern-primary w-full flex items-center justify-center gap-3
              disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
              disabled:shadow-none disabled:bg-secondary-300"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Play className="w-6 h-6" />
            )}
            <span className="text-lg">開始計時</span>
          </button>
        ) : (
          // 停止按鈕
          <button
            onClick={onStop}
            disabled={isLoading}
            className="btn-modern-danger w-full flex items-center justify-center gap-3
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Square className="w-6 h-6" />
            )}
            <span className="text-lg">停止計時</span>
          </button>
        )}
        
        {/* 狀態提示 */}
        {!isLoading && !isRunning && (!selectedProject || !description.trim()) && (
          <div className="flex items-center justify-center gap-2 py-3 px-4 
            bg-amber-50/80 border border-amber-200/50 rounded-xl">
            <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-amber-700">
              {!description.trim() && !selectedProject 
                ? '請輸入任務描述並選擇專案'
                : !description.trim() 
                  ? '請輸入任務描述' 
                  : '請選擇專案'}
            </span>
          </div>
        )}
        
        {isLoading && (
          <div className="flex items-center justify-center gap-3 py-3 px-4 
            bg-blue-50/80 border border-blue-200/50 rounded-xl">
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" />
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>
            <span className="text-sm font-medium text-blue-700">處理中...</span>
          </div>
        )}
      </div>
      
      {/* 快捷鍵提示 */}
      <div className="mt-6 pt-4 border-t border-secondary-200/50">
        <div className="flex items-center justify-center gap-4 text-xs text-secondary-500">
          <div className="flex items-center gap-1.5">
            <kbd className="kbd-badge">Space</kbd>
            <span>開始/停止</span>
          </div>
          <div className="flex items-center gap-1.5">
            <kbd className="kbd-badge">Esc</kbd>
            <span>清空</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TimerOperationCard
